"use client";

import Image from "next/image";
import { HOME_COPY } from "@/lib/landing-copy";
import { DATA_LABEL_CLS, DISPLAY, EDITORIAL_ACTION_CLS, LABEL_CLS } from "./theme";
import { LogoGrid } from "./primitives";
import { MERCURY_CLIENTS } from "./clients";
import { EvidenceSourcesGrid } from "./evidence-sources-grid";
import { IngressLayer, LiveInstrument } from "@/app/concept/groups/hero-instrument";
import { CtaPair } from "@/app/concept/groups/pages";

const INK_MUTED = "text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

export type LandingHeroContent = {
  label: string;
  title: readonly [string, string, string];
  lead: readonly (string | { name: string; logo: string })[];
  cta: string;
  buyer: string;
  instrumentLabel: string;
  /** A sector's arrangement of the connector grid; without one, the full grid. */
  integrations?: "accountants" | "consultancies";
};

/** The clients Mercury Labs has delivered for. The Crown Commercial Service
 *  mark is a framework status, not a client, so the home's trust band
 *  carries it instead (the 2026-09-30 copy review). */
const DELIVERED_FOR = MERCURY_CLIENTS.filter((client) => client.name !== "Crown Commercial Service");

/** The client roster, light variant with hairlines, as landing furniture. */
export function LogoStrip() {
  return (
    <section className="border-b border-dotted border-[var(--rule)] py-10">
      <p className={`${INK_MUTED} ${DATA_LABEL_CLS}`}>Mercury Labs has delivered for</p>
      <div className="mt-5">
        <LogoGrid variant="light" clients={DELIVERED_FOR} />
      </div>
      <p className={`mt-4 ${INK_MUTED} text-[0.72rem] leading-[1.5]`}>
        Client work by Mercury Labs, the team behind your Twin.
      </p>
    </section>
  );
}

export function WorkingPageHero({ production = false, content }: { production?: boolean; content?: LandingHeroContent } = {}) {
  return (
    <header
      id={content ? "hero" : undefined}
      data-twin-stage
      className="relative isolate border-b border-dotted border-[var(--rule)]"
    >
      <IngressLayer />
      <div className={`relative z-[2] pb-14 sm:pb-16 ${production ? "pt-32 sm:pt-36" : "pt-14 sm:pt-16"}`}>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-center">
          <div>
            {content && <p className={`${LABEL_CLS} mb-5 text-[var(--theme-accent-text)]`}>Your Twin / {content.label}</p>}
            <h1
              style={DISPLAY}
              className={`${INK_88} max-w-[12ch] text-[clamp(2.9rem,6.2vw,5rem)] leading-[0.98] font-semibold tracking-[-0.04em]`}
            >
              {(content?.title ?? HOME_COPY.headline).map(line => <span className="block" key={line}>{line}</span>)}
            </h1>
            <p className={`mt-5 max-w-[50ch] ${INK_72} text-[1.05rem] leading-[1.65]`}>
              {content ? content.lead.map(part => typeof part === "string" ? part : (
                <span key={part.name} className="inline-flex items-baseline gap-[0.3em] whitespace-nowrap">
                  <Image src={part.logo} alt="" width={18} height={18} className="relative top-[0.12em] size-[1em] shrink-0 object-contain" />
                  {part.name}
                </span>
              )) : HOME_COPY.lead}
            </p>
            {/* One action and one way to read on: the step list and the
                changelog link left the hero in the 2026-09-30 copy review. */}
            <div className="mt-7">
              {content ? <div className="flex flex-wrap items-center gap-3">
                <a href="#waitlist" data-cta-placement="hero" className={`${EDITORIAL_ACTION_CLS} bg-[var(--theme-dark)] text-[var(--theme-ground)]`}>{content.cta}</a>
                <a href="#workflow" className={`${EDITORIAL_ACTION_CLS} border border-[var(--rule)] ${INK_88}`}>Why a Twin?</a>
              </div> : <CtaPair />}
            </div>
            {content && <p className={`mt-5 text-[0.72rem] leading-relaxed ${INK_MUTED}`}>{content.buyer}</p>}
          </div>
          <LiveInstrument
            sizeClass="w-[min(70vw,29rem)]"
            label={content?.instrumentLabel ?? "Your Twin · From records to useful work"}
          />
        </div>
        {/* The home hero's connector grid, riser and all: the systems below
            feed the instrument above, on the same sequence clock. The lg
            margin leaves the riser's 140px clear of the row above. */}
        <div className="mt-10 lg:mt-36">
          <EvidenceSourcesGrid showRiser variant={content?.integrations} />
        </div>
      </div>
    </header>
  );
}
